import { FC, useEffect, useState } from 'react'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { useTheme } from '../../../../../../_metronic/partials/layout/app-theme/ThemeContext'
import Table from '../../common/Table'
import TableHeader from '../../common/TableHeader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../../redux/store'
import {
  getGiftGalleryListApi,
  deleteGiftGalleryApi,
} from '../../../../../redux/slices/GiftGallerySlice'
import { toast } from 'react-toastify'


const GiftGalleryTable: FC = () => {
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchKey, setSearchKey] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedRow, setSelectedRow] = useState([])
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { theme } = useTheme()

  const giftGalleryData = useSelector(
    (state: RootState) => state.giftGallery.GiftGalleryList?.data?.data || []
  )
  const giftGalleryTotal = useSelector(
    (state: RootState) => state.giftGallery.GiftGalleryList?.data?.total || 0
  )

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchKey)
    }, 1000)

    return () => clearTimeout(debounceTimeout)
  }, [searchKey])

  const fetchGiftGalleryList = () => {
    setLoading(true)
    dispatch(
      getGiftGalleryListApi({
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterkey: debouncedSearchQuery,
      })
    )
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error('Failed to fetch gift gallery data')
      })
  }

  useEffect(() => {
    fetchGiftGalleryList()
  }, [currentPage, rowsPerPage, debouncedSearchQuery])

  const handleEdit = (id: any) => {
    navigate(`edit/${id}`)
  }
  const handleView = async (id: any) => {
    try {
      const response: any = await dispatch(
        getGiftGalleryListApi({
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          filterkey: "",
        })
      );
  
      const selectedGift = response.payload?.data?.data?.find((gift: any) => gift.id === id);
      const gift_image_url = selectedGift?.gift_image_url || selectedGift?.gift_image;
  
      if (gift_image_url) {
        const newWindow = window.open("", "_blank");
        newWindow?.document.write(`
          <html>
            <head>
              <title>Gift Image</title>
            </head>
            <body style="text-align: center; margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; height: 100vh;">
              <img src="${gift_image_url}" alt="Gift Image" style="max-width: 80%; max-height: 80%; border: 1px solid #ccc;"/>
            </body>
          </html>
        `);
      } else {
        toast.error("Image not found");
      }
    } catch (error) {
      toast.error("Error fetching gift data");
    }
  };
  

  const handleDelete = (id: any) => {
    dispatch(deleteGiftGalleryApi(id)).then((response: any) => {
      if (response.payload.success) {
        toast.success(response.payload.reason)
        fetchGiftGalleryList()
      } else {
        toast.error(response.payload.reason || 'Something went wrong')
      }
    })
  }

  const handleSearchChange = (searchQuery: string) => {
    setSearchKey(searchQuery)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setCurrentPage(page)
    setRowsPerPage(newPerPage)
  }

  const columnData = [
    { name: 'Name', selector: (row: any) => row?.name, sortable: true },
    { name: 'Required Points', selector: (row: any) => row?.required_points, sortable: true },
    { name: 'Required Money', selector: (row: any) => row?.required_money, sortable: true },
    // {name: 'Description', selector: (row: any) => row?.description, sortable: true},
    {
      name: 'Valid Upto',
      selector: (row: any) => row?.valid_upto ? new Date(row?.valid_upto).toLocaleDateString() : 'N/A',
      sortable: true,
    },
    {
      name: 'Created At',
      selector: (row: any) => new Date(row?.created_at).toLocaleDateString(),
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleView(row?.id)}
          >
            <KTIcon iconName='eye' className='fs-3' /> 
          </div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleEdit(row?.id)}
          >
            <KTIcon iconName='pencil' className='fs-3' />
          </div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleDelete(row?.id)}
          >
            <KTIcon iconName='trash' className='fs-3' />
          </div>
        </div>
      ),
      sortable: false,
      style: { textAlign: 'left' },
    },
  ]

  return (
    <div className='card'>
      <TableHeader
        search={searchKey}
        addButtonText={'Add Gift'}
        handleSearchChange={handleSearchChange}
        handleAddButton={() => navigate('add')}
        showFilter={true}
        filterContent={<p>Custom filter form content here</p>}
      />

      <div className='card-body p-0 shadow-sm'>
        <Table
          data={giftGalleryData}
          columns={columnData}
          isLoading={loading}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          totalRows={giftGalleryTotal}
          showPagination={true}
          onRowClicked={(row) => {
          }}
          selectableRows
          onSelectedRowsChange={(selected) => {
            setSelectedRow(selected.selectedRows)
          }}
        />
      </div>
    </div>
  )
}

export default GiftGalleryTable
